import { router } from "@inertiajs/react"
import { Clock } from "@phosphor-icons/react"
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import ReactMarkdown from "react-markdown"

import receptionistAvatar from "@/assets/receptionist_avatar.png"
import {
  AgentPurchaseBody,
  type ChargeResult,
} from "@/components/billing/agent-purchase-modal"
import {
  type TrialInfo,
  formatShortDate,
  formatUsd,
} from "@/components/billing/agent-purchase-summary"
import type { IntentPayload } from "@/components/billing/stripe-payment-modal"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import { activateAgentsPath, activationAgentsPath } from "@/routes"

// IMP-465: adding a job from Home happens in one modal — a pitch step carrying
// the activation panel's layout (duration chip, name, copy + bullets, dashed
// rule, price/trial above the action) beside a graphic of the job's own
// output, then the existing purchase confirmation swapped into the same modal
// (no second dialog stacks over the first). The modal is anchored a fixed
// distance from the top so the header stays put while the height animates
// between steps.

interface Archetype {
  uid: string
  slug: string | null
  name: string
  description: string | null
  long_description: string | null
  duration: string | null
  activation_graphic_url: string | null
}

interface Pricing {
  monthly_cents: number
  due_today_cents: number
  prorated: boolean
  trial?: TrialInfo | null
}

interface Payload {
  redirect_to?: string
  archetype?: Archetype
  retail_price_cents?: number | null
  includes?: string[]
  pricing?: Pricing | null
  saved_card?: { brand: string; last4: string } | null
}

async function postJson<T>(url: string, body?: unknown): Promise<T> {
  const csrf =
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") ?? ""
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": csrf,
    },
    credentials: "same-origin",
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  const data = (await response.json()) as T & { error?: string }
  if (!response.ok) {
    throw new Error(data.error ?? "Something went wrong. Please try again.")
  }
  return data
}

// ---------------------------------------------------------------------------
// Animated height — CSS can't animate an auto height, so measure the content
// and transition an explicit pixel height between steps.
// ---------------------------------------------------------------------------
function AnimatedHeight({ children }: { children: React.ReactNode }) {
  const inner = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState<number | "auto">("auto")

  useEffect(() => {
    const el = inner.current
    if (!el) return
    const observer = new ResizeObserver(() => {
      setHeight(el.offsetHeight)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      className="overflow-hidden transition-[height] duration-300 ease-in-out"
      style={{ height: height === "auto" ? undefined : height }}
    >
      <div ref={inner}>{children}</div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// The job graphic — the same illustration model and container as the old
// activation page's panel: the archetype's uploaded graphic (IMP-244), the
// built-in illustration as fallback, with the top/bottom fade.
// ---------------------------------------------------------------------------
function JobGraphic({ archetype }: { archetype: Archetype }) {
  return (
    // Decorative only, and its fixed 340px column would crowd out the pitch
    // content on a phone — hidden below `md`.
    <div className="border-border relative ml-8 hidden w-[340px] shrink-0 overflow-hidden rounded-l-xl border border-r-0 md:block">
      <img
        src={archetype.activation_graphic_url ?? receptionistAvatar}
        alt={`${archetype.name} agent`}
        className="h-full w-full object-cover object-left"
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(to bottom, rgba(250,250,250,0.5), rgba(250,250,250,0.08) 40%, rgba(250,250,250,0.08) 60%, rgba(250,250,250,0.5))",
        }}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// The modal
// ---------------------------------------------------------------------------
export function AddJobModal({
  archetypeUid,
  onClose,
  billingBlocked,
  partnerName,
  imperativeManaged,
}: {
  // Open when non-null; the uid drives the payload fetch.
  archetypeUid: string | null
  onClose: () => void
  // IMP-195: imperative-managed partners can't take payment until Stripe
  // payouts are ready.
  billingBlocked: boolean
  partnerName: string | null
  imperativeManaged: boolean
}) {
  const [payload, setPayload] = useState<Payload | null>(null)
  const [step, setStep] = useState<"pitch" | "confirm">("pitch")
  const [subscribing, setSubscribing] = useState(false)

  const open = archetypeUid != null

  useEffect(() => {
    if (!archetypeUid) return
    setPayload(null)
    setStep("pitch")
    let cancelled = false
    fetch(activationAgentsPath(archetypeUid), {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then((r) => r.json())
      .then((data: Payload) => {
        if (cancelled) return
        // An already-purchased job routes to its own page.
        if (data.redirect_to) {
          router.visit(data.redirect_to)
          return
        }
        setPayload(data)
      })
      .catch(() => {
        if (!cancelled) onClose()
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archetypeUid])

  const archetype = payload?.archetype
  const pricing = payload?.pricing ?? null
  const includes = payload?.includes ?? []
  const canPurchase = imperativeManaged && pricing != null
  const priceLabel =
    payload?.retail_price_cents != null
      ? formatUsd(payload.retail_price_cents)
      : null
  const trial = pricing?.trial ?? null

  // Purchase behavior is unchanged — these are the same endpoints the
  // activation page called (IMP-195/389/394).
  function fetchPaymentIntent(): Promise<IntentPayload> {
    return postJson<IntentPayload>(
      `/agents/activate/${archetypeUid}/payment_intent`,
    )
  }

  async function fulfillPayment(paymentIntentId: string) {
    const { redirect_to } = await postJson<{ redirect_to: string }>(
      `/agents/activate/${archetypeUid}/fulfill`,
      { payment_intent_id: paymentIntentId },
    )
    router.visit(redirect_to)
  }

  function chargeSavedCard(): Promise<ChargeResult> {
    return postJson<ChargeResult>(`/agents/activate/${archetypeUid}/charge`)
  }

  function fetchSetupIntent(): Promise<IntentPayload> {
    return postJson<IntentPayload>(
      `/agents/activate/${archetypeUid}/trial_setup_intent`,
    )
  }

  function startTrial(
    setupIntentId: string | null,
  ): Promise<{ redirect_to: string }> {
    return postJson<{ redirect_to: string }>(
      `/agents/activate/${archetypeUid}/start_trial`,
      setupIntentId ? { setup_intent_id: setupIntentId } : undefined,
    )
  }

  // Partner-managed: no payment, provision directly.
  function subscribe() {
    setSubscribing(true)
    router.post(
      activateAgentsPath(),
      { archetype_uid: archetypeUid },
      { onFinish: () => setSubscribing(false) },
    )
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className={cn(
          // Anchored a fixed distance from the top so the header stays put as
          // the height changes between steps.
          "top-24 translate-y-0 gap-0 overflow-hidden p-0 transition-[max-width] duration-300",
          step === "pitch" ? "sm:max-w-3xl" : "sm:max-w-lg",
        )}
      >
        <AnimatedHeight>
          {!payload || !archetype ? (
            <div className="flex h-64 items-center justify-center">
              <DialogHeader className="sr-only">
                <DialogTitle>Add a job</DialogTitle>
              </DialogHeader>
              <Loader2 className="text-muted-foreground size-5 animate-spin" />
            </div>
          ) : step === "pitch" ? (
            <div className="flex min-h-[420px] py-6 pl-6">
              {/* Left: the activation panel's layout */}
              <div className="flex flex-1 flex-col">
                <DialogHeader className="sr-only">
                  <DialogTitle>{archetype.name}</DialogTitle>
                </DialogHeader>
                {archetype.duration && (
                  <span className="mb-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700 dark:bg-blue-950/60 dark:text-blue-300">
                    <Clock className="size-3 shrink-0" weight="fill" />
                    {archetype.duration}
                  </span>
                )}
                <h2 className="text-xl font-semibold tracking-tight">
                  {archetype.name}
                </h2>
                {(archetype.long_description ?? archetype.description) && (
                  <div className="text-muted-foreground mt-3 text-sm leading-relaxed">
                    <ReactMarkdown
                      components={{
                        p: ({ children }) => <p className="mb-4">{children}</p>,
                        ul: ({ children }) => (
                          <ul className="marker:text-muted-foreground mb-6 list-disc space-y-2 pl-4">
                            {children}
                          </ul>
                        ),
                        li: ({ children }) => (
                          <li className="pl-1">{children}</li>
                        ),
                      }}
                    >
                      {archetype.long_description ??
                        archetype.description ??
                        ""}
                    </ReactMarkdown>
                  </div>
                )}

                <div className="flex-1" />

                {trial ? (
                  <div className="mt-5">
                    <p className="text-2xl font-bold tracking-tight">
                      {trial.days} days free
                    </p>
                    {priceLabel && (
                      <p className="text-muted-foreground mt-1 text-sm">
                        then {priceLabel}/month, starting{" "}
                        {formatShortDate(trial.trial_end_date)}
                      </p>
                    )}
                  </div>
                ) : priceLabel ? (
                  <div className="mt-5 flex items-baseline gap-1">
                    <span className="text-2xl font-bold tracking-tight">
                      {priceLabel}
                    </span>
                    <span className="text-muted-foreground text-sm">
                      / month
                    </span>
                  </div>
                ) : null}

                <div className="border-border my-5 border-t border-dashed" />

                <div>
                  <Button
                    onClick={() => setStep("confirm")}
                    disabled={billingBlocked}
                  >
                    Continue
                    <ArrowRight className="size-4" />
                  </Button>
                </div>
                {billingBlocked && (
                  <p className="text-muted-foreground mt-3 text-sm">
                    Online payments aren&rsquo;t enabled for your account yet.
                    Contact {partnerName ?? "your provider"} to finish billing
                    setup.
                  </p>
                )}
              </div>

              <JobGraphic archetype={archetype} />
            </div>
          ) : (
            <div className="p-6">
              <DialogHeader className="mb-4">
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground -ml-2 size-7"
                    onClick={() => setStep("pitch")}
                    aria-label="Back"
                  >
                    <ArrowLeft className="size-4" />
                  </Button>
                  <DialogTitle>Confirm order</DialogTitle>
                </div>
                {!canPurchase && (
                  <DialogDescription>
                    Your agent will go live once you&apos;ve completed setup.
                  </DialogDescription>
                )}
              </DialogHeader>

              {canPurchase && pricing ? (
                <AgentPurchaseBody
                  active={open && step === "confirm"}
                  onClose={onClose}
                  agentName={archetype.name}
                  monthlyCents={pricing.monthly_cents}
                  includes={includes}
                  prorated={pricing.prorated}
                  dueTodayCents={pricing.due_today_cents}
                  savedCard={payload.saved_card ?? null}
                  trial={trial}
                  fetchIntent={fetchPaymentIntent}
                  onCharge={chargeSavedCard}
                  onFulfill={fulfillPayment}
                  fetchSetupIntent={fetchSetupIntent}
                  onStartTrial={startTrial}
                />
              ) : (
                <div className="space-y-4">
                  {priceLabel && (
                    <div className="bg-muted flex flex-col items-start gap-2 rounded-xl px-6 py-5">
                      <div className="flex items-baseline gap-1.5">
                        <p className="text-3xl font-bold tracking-tight">
                          {priceLabel}
                        </p>
                        <p className="text-muted-foreground text-sm">/ month</p>
                      </div>
                      <p className="text-muted-foreground text-sm">
                        Starts today. Billed monthly through your account.
                        Cancel anytime.
                      </p>
                      {includes.length > 0 && (
                        <ul className="text-muted-foreground list-disc space-y-0.5 pl-5 text-sm">
                          {includes.map((line) => (
                            <li key={line}>Includes {line}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                  <DialogFooter>
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={onClose}
                      disabled={subscribing}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="flex-[2]"
                      onClick={subscribe}
                      disabled={subscribing}
                    >
                      {subscribing
                        ? "Subscribing…"
                        : `Subscribe${priceLabel ? ` · ${priceLabel}/mo` : ""}`}
                    </Button>
                  </DialogFooter>
                </div>
              )}
            </div>
          )}
        </AnimatedHeight>
      </DialogContent>
    </Dialog>
  )
}
